import React, { useEffect, useRef, useState } from 'react';
import { TextField, InputAdornment, Button, Radio } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterDropdown from './FilterDropdown';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { formatChatDate } from '../../utils/dateTime';
import Loader from '../common/Loader/Loader';
import { ListError } from './ListStatus';
import { renderLastMessage } from '../../utils/chatUtils';
import { useMemo } from 'react';
import { getAvatarByGender } from '../../utils/CommonUtils';
import { DisablesAccountIcon } from '../../../src/icons/index';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  maxHeight: '80vh', // Restricts height for scroll
  overflowY: 'auto', // Adds vertical scroll
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '20px',
  p: '20px 20px 0 20px',
};

// Common style object for FormControlLabel and Checkbox
const checkboxStyle = {
  display: 'flex',
  // justifyContent: 'space-between',
  width: '100%',
  marginLeft: '0',
  fontSize: '14px', // Label font size
  '.MuiTypography-root': {
    fontSize: '14px', // Label text font size
  },
  control: {
    color: '#916008', // Unchecked color
    '&.Mui-checked': {
      color: '#916008', // Checked color
    },
  },
};

export const filters = [
  'All',
  // 'New Messages',
  'Unread Messages'
]

const ChatListUser = ({
  chatData,
  onChatClick,
  selectedFilter,
  setSelectedFilter,
  searchTerm,
  setSearchTerm,
  handleSearch,
  setUsers,
  loading,
  selectedChatBot,
  selectedChat,
  error,
  onRetry
}) => {
  const [filterType,setFilterType] = useState(selectedFilter)
  const [pinnedIds, setPinnedIds] = useState(() => new Set());

  // Derived from the prop, NOT mirrored into state. This used to be a `chats` state kept
  // in sync by an effect, which meant every prop change rendered ONCE with the previous
  // bot's list before the effect swapped it in. React 18 batches the container's
  // setUsers(newList) with setUsersLoading(false), so that stale render landed with the
  // loader already gone -- the fraction-of-a-second flash of the old bot's users after
  // switching bots. Deriving removes the intermediate render entirely.
  const chats = useMemo(() => {
    const list = chatData || [];
    if (!pinnedIds.size) return list;
    return [...list].sort(
      (a, b) => (pinnedIds.has(b?._id) ? 1 : 0) - (pinnedIds.has(a?._id) ? 1 : 0)
    );
  }, [chatData, pinnedIds]);

  // Mirrors the bot column: keep the open thread visible when it is selected. Keyed on
  // the id alone so an incoming message (which rewrites lastMessage in place via
  // updateUser) never re-scrolls a list someone is mid-read on.
  const selectedRowRef = useRef(null);
  const lastScrolledIdRef = useRef(null);
  useEffect(() => {
    const id = selectedChat?._id;
    if (!id || lastScrolledIdRef.current === id) return;
    let frames = 0;
    let raf;
    const tryScroll = () => {
      const el = selectedRowRef.current;
      if (el?.offsetParent) {
        el.scrollIntoView({ block: 'nearest' });
        lastScrolledIdRef.current = id;
        return;
      }
      if (++frames < 10) raf = requestAnimationFrame(tryScroll);
    };
    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
  }, [selectedChat?._id, chats]);

  // Function to handle pinning/unpinning
  // Same net effect as before (pinned sort to the top) without mutating the objects the
  // parent owns, which the old version did to survive being re-derived from the prop.
  const handlePinToggle = (chat) => {
    setPinnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(chat?._id)) next.delete(chat?._id);
      else next.add(chat?._id);
      return next;
    });
  };
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value || '');
  };
  const filteredChats = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return chats;

    return chats.filter(chat => {
      const username = (chat?.participantId?.firstName || chat?.participantId?.userName) || '';
      return username.toLowerCase().includes(term);
    });
  }, [chats, searchTerm]);

  useEffect(()=>{
      setFilterType(selectedFilter)
  },[selectedChatBot?._id])
  return (
    <div className="chatlist border-start">
      <div className="modal-dialog-scrollable">
        {/* Conditionally render based on whether there are filtered chats */}

        <div className="modal-content ">
          <div className="chat-header p-16">
            <div className="msg-search w-100">
              <TextField
                type="text"
                id="inlineFormInputGroup"
                placeholder="Search"
                aria-label="search"
                value={searchTerm}
                onChange={handleFilterChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"
                      onClick={handleSearch}
                    >
                      <SearchIcon sx={{ width: '18px', height: '18px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '32px',
                    fontSize: '14px',
                    color: '#8FA0AF',
                    '&:hover fieldset': {
                      borderColor: '#FF4A4A',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF4A4A',
                    },
                    '& input': {
                      padding:'5.5px 14px 5.5px 0',
                    },
                  },
                  '& .MuiInputAdornment-root': {
                    color: 'grey',
                    '&:hover': {
                      color: '#FF4A4A',
                    },
                  },
                }}
              />
            </div>
            <div className="hdtopsetup pt-16">
             {/* WEB-48: this used to be the static string "Users Messages", so nothing on
                 screen ever named the bot whose inbox you were in — the ChatBox header
                 shows the member you are talking TO, never who you are speaking AS. The
                 bot was already available here as a prop; it was just never rendered.
                 Unlike the row highlight this survives scrolling and list reordering. */}
             {selectedChatBot ? (
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                 <img
                   src={selectedChatBot?.profilePicture || getAvatarByGender(selectedChatBot?.gender)}
                   alt=""
                   style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                 />
                 <Box sx={{ minWidth: 0 }}>
                   <Typography
                     noWrap
                     title={selectedChatBot?.firstName ?? selectedChatBot?.userName}
                     sx={{ color: '#333', fontSize: '16px', fontWeight: '500', lineHeight: 1.2 }}
                   >
                     {selectedChatBot?.firstName ?? selectedChatBot?.userName}
                   </Typography>
                   <Typography sx={{ color: '#A0A0A0', fontSize: '11px', lineHeight: 1.2 }}>
                     Bot inbox
                   </Typography>
                 </Box>
               </Box>
             ) : (
               <Typography sx={{color:'#808080', fontSize:"16px", fontWeight:'500'}}>Users Messages</Typography>
             )}
              <div className="font-12 fw-medium color7b d-flex justify-content-between align-items-center">
                {selectedFilter}{' '}
                <Button
                  onClick={handleOpen}
                  sx={{ padding: '5px', minWidth: '40px' }}
                >
                  <img
                    src="/assets/images/messages/filter-icon.svg"
                    className="img-fluid"
                    alt=""
                  />
                </Button>
                {/* <FilterDropdown /> */}
              </div>
            </div>
          </div>
          {error ? <ListError onRetry={onRetry} /> : loading ? <Loader /> : <>
            {filteredChats.length > 0 ? (
              <div className="modal-body p-0 overflowauto messagesList">
                <div className="chat-lists">
                  <div className="chat-list">
                    {filteredChats.map((chat, index) => {
                      const isSelected =
                        !!selectedChat?._id && selectedChat?._id === chat?._id;
                      return <div
                        key={chat?._id ?? index}
                        ref={isSelected ? selectedRowRef : null}
                        className={`d-flex align-items-center flex-row py-16 px-24 conversation gap-10${
                          isSelected ? ' selected' : ''
                        }`}
                        onClick={() => onChatClick(chat)}
                      >
                        <div className="flex-shrink-0 cs-avatar">
                          {chat?.participantId?.isInvisible ? (
                            <DisablesAccountIcon className='img-fluid'/>
                          ) :
                            <img
                              className="img-fluid"
                              src={chat.participantId?.profilePicture || getAvatarByGender(chat.participantId?.gender)}
                              alt="user img"
                            />
                          }
                          {chat?.participantId?.isOnLine ?
                            <span className="active" /> : null}
                        </div>
                        <div className="flex-grow-1 conversation_content">
                          <h3>{chat?.participantId ?
                            (chat?.participantId?.isInvisible ?  "Disabled Account" : (chat?.participantId?.firstName ?? chat?.participantId?.userName)) : 'User Deleted'}</h3>
                          <p>{chat?.participantId?.isInvisible ? (
                            <p>This profile has been removed because it does not meet our platform standards.</p>
                          ) : renderLastMessage(chat?.lastMessage?.message, chat?.lastMessage?.messageType)}
                          </p>
                        </div>
                        <div className="conversationTimeUnreadPin">
                          <div className="time text-nowrap d-flex align-items-center justify-content-end">
                            {chat?.updatedAt
                              ? formatChatDate(chat?.updatedAt)
                              : ''}
                          </div>
                          <div className="conversationPin_unread d-flex align-items-center justify-content-end gap-1">
                            <div
                              className="pin-unpinBg"
                              style={{ height: '22px', overflow: 'hidden' }}
                            >
                              <Button
                                className={`pin-unpin ${chat?.isPinned ? 'show' : ''
                                  }`}
                                sx={{
                                  minWidth: '30px',
                                  padding: '0',
                                  height: '20px',
                                  lineHeight: '27px',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePinToggle(chat);
                                }}
                              >
                                <img
                                  src={
                                    chat?.isPinned
                                      ? '/assets/images/messages/pinned.svg'
                                      : '/assets/images/messages/pin.svg'
                                  }
                                  className="img-fluid"
                                  alt="pin icon"
                                />
                              </Button>
                            </div>
                            {chat?.unreadCount > 0 && (
                              <div className="conversation_unread">
                                {chat?.unreadCount}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="modal-body overflowauto emptyList">
                <div className="text-center d-flex justify-content-center align-items-center flex-column h-100 p-20">
                  <span>
                    <img
                      src="/assets/images/messages/no-messages-yet.svg"
                      className="img-fluid"
                      alt=""
                      style={{maxWidth:'70%', marginLeft:'auto', marginRight:'auto'}}
                    />
                  </span>
                  <h3 className="font-18 font-p fw-medium text-black mt-3">
                    No Messages Yet?
                  </h3>
                  <p className="font-12 font-m fw-normal text-black">
                    Don’t worry – your perfect match might just be a conversation
                    away. Stay active and discover exciting connections! Once you
                    receive your messages, make sure to use the filters above.
                  </p>
                </div>
              </div>
            )}</>}
        </div>
      </div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {/* Filters */}
          <FormGroup>
            {filters.map((label, index) => (
              <FormControlLabel
                key={index}
                sx={checkboxStyle}
                control={<Radio
                  onChange={() => {
                    setFilterType(label)
                  }}
                  checked={filterType === label ? true : false}
                />}
                label={label}
                // labelPlacement="start"
                
                
              />
            ))}
          </FormGroup>

          {/* Buttons */}
          <Box
            className="border-top mt-16 position-sticky bottom-0 z-3 bg-base pb-16"
            sx={{ marginTop: '20px', background: '#fff' }}
          >
            <Button
              onClick={()=>{handleClose(); setSelectedFilter('All');setFilterType('')}}
              className="w-100"
              sx={{
                background: '#fff',
                color: '#000',
                fontSize: '16px',
                textTransform: 'capitalize',
                fontFamily: 'Neue Montreal',
                width: '100%',
                marginBottom: '10px',
              }}
            >
              Reset
            </Button>
            <Button
              onClick={()=>{handleClose();setSelectedFilter(filterType || "All")}}
              className="w-100"
              sx={{
                background: '#916008',
                color: '#fff',
                fontSize: '16px',
                textTransform: 'capitalize',
                fontFamily: 'Neue Montreal',
                width: '100%',
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default ChatListUser;
